import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">&ldquo;{quote}&rdquo;</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">&mdash; {source}</cite>
    </blockquote>
  );
}

const faqs = [
  {
    question: 'Does Bexley\'s GREEN DfT condition rating mean I cannot claim?',
    answer: 'No. GREEN means Bexley performs above average on network-level condition and spend — but Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired. The borough\'s own report shows U-road RED condition rose from 2.31% to 12.10% between 2020 and 2024, pothole fills climbed 83% to 2,595 in 2023/24, and the council attributes 2023–2024 deterioration partly to defects from December 2022 that "slowly deteriorated with weather and use."',
  },
  {
    question: 'What if my pothole was on a residential or unclassified road?',
    answer: 'Unclassified roads make up 450.79km — 79% of Bexley\'s 570.62km carriageway network. The transparency report publishes RED-condition percentages for U-roads only (12.10% in 2024 — roughly 54.5km needing maintenance), not a full red/amber/green breakdown. Most Bexley pothole damage happens on these residential streets. Prior reports via FixMyStreet (fix.bexley.gov.uk) and photos showing defect age matter more than network-wide GREEN scorecards.',
  },
  {
    question: 'Why is best practice rated AMBER when condition and spend are GREEN?',
    answer: 'The DfT best-practice scorecard is separate from condition and spend. Bexley maps its five-category condition assessment to the legacy red/amber/green system required by the DfT, uses camera-based scanning alongside twice-yearly foot inspections, and runs FixMyStreet handling around 16,000 reports a year — but the Department for Transport still rates wider best practice AMBER overall, contributing to Bexley\'s AMBER headline rating despite GREEN condition and spend scorecards.',
  },
  {
    question: 'Bexley projects capital spend five times its DfT allocation — does that block my claim?',
    answer: 'Not automatically. Projected 2025/26 capital spend is £4.51m against a DfT allocation of £895,000 — a GREEN spend scorecard. Section 58 turns on the specific defect, not aggregate budgets. With 61% preventative spend projected (down from 72% in 2022/23) and 39% reactive, your pothole may sit in the reactive third the council budgets for emergency repairs rather than prevention.',
  },
  {
    question: 'Pothole repairs rose 83% to 2,595 — does that help my case?',
    answer: 'It can. Bexley filled an estimated 1,414 potholes in 2020/21 and 2,595 in 2023/24 — an 83% rise — maintaining 2,590 fills in 2024/25. The council records total highway defects treated and estimates pothole counts from reactive maintenance works. That volume shows defects forming continuously across the network, not proof your specific defect was unavoidable or caught within inspection intervals.',
  },
  {
    question: 'What about concrete slab roads and defects below intervention thresholds?',
    answer: 'Bexley\'s report explains many residential roads use concrete slabs with a thin 15–25mm asphalt overlay. Reflective cracking and patches revealing lighter concrete beneath may fall below the council\'s defined maintenance intervention thresholds — the council states this "does not mean the road is unsafe." If your pothole exceeded those thresholds or caused damage, the council still needs to show it inspected and responded reasonably; threshold policy alone does not defeat a claim with strong evidence.',
  },
  {
    question: 'How do I report a pothole to London Borough of Bexley?',
    answer: 'Report road and street defects via FixMyStreet at fix.bexley.gov.uk, or through the council\'s online reporting form at bexley.gov.uk. The council\'s transparency report states FixMyStreet handles around 16,000 reports each year. Prior reports of the same defect strengthen a claim by demonstrating the council had notice before your incident. Fixtyer searches for existing reports and attaches them to your claim pack.',
  },
];

export function BexleyCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Bexley Pothole Claims | 12.10% of U-Roads in RED Condition | Fixtyer"
        description="London Borough of Bexley: DfT AMBER overall but GREEN on condition and spend. U-road RED condition up 424% since 2020, 2,590 pothole fills in 2024/25. What this means for your Section 58 claim."
        canonical="/councils/bexley"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Bexley', url: '/councils/bexley' },
        ]}
      />
      <FAQSchema faqs={faqs} />
      <ServiceSchema
        councilName="London Borough of Bexley"
        councilSlug="bexley"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/bexley"
        headline="Bexley Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Bexley</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            12.10% of Bexley&apos;s Residential Roads Now in RED Condition
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            London Borough of Bexley earns <strong className="text-emerald-400">GREEN</strong> DfT scorecards for
            condition and spend — yet its own transparency report records{' '}
            <strong className="text-rose-400">U-road RED condition rising from 2.31% to 12.10%</strong> between 2020
            and 2024, <strong className="text-rose-400">2,590 pothole fills in 2024/25</strong> (up 83% from 2020/21),
            and preventative maintenance projected to fall to <strong className="text-amber-400">61%</strong>. The DfT
            rates the borough <strong className="text-amber-400">AMBER</strong> overall on best-practice gaps. Section
            58 still turns on your specific defect.
          </p>

          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">424%</div>
            <div className="text-white font-medium mt-2">Rise in U-road RED condition since 2020</div>
            <div className="text-brand-300 text-sm mt-1">
              From 2.31% to 12.10% — roughly 54.5km of residential network the council defines as poor and
              &ldquo;should be considered for maintenance&rdquo; — on a borough where 79% of roads are unclassified.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* Network scale */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            570.62km of Roads — Mostly Residential
          </h2>
          <p className="text-surface-600 mb-8">
            Network size from Bexley&apos;s 2025 transparency report — where pothole claims actually happen
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">570.62km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Total carriageway</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-amber-600">450.79km</div>
              <div className="text-sm text-amber-800 font-medium mt-1">Unclassified (U) roads</div>
              <div className="text-xs text-amber-700 mt-1">79% of the network</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">79.93km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">A roads</div>
              <div className="text-xs text-surface-500 mt-1">TfL maintains A2 and A20</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">39.90km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">B and C roads</div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-6">
            <table className="w-full text-sm">
              <caption className="sr-only">Bexley highway asset breakdown</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left font-semibold text-brand-900">Asset</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Scale</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Footways</td>
                  <td className="px-6 py-4 text-center font-medium">767.74km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Public rights of way</td>
                  <td className="px-6 py-4 text-center font-medium">59.61km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Cycleways (national routes)</td>
                  <td className="px-6 py-4 text-center font-medium">6.22km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Highway structures</td>
                  <td className="px-6 py-4 text-center font-medium">174 (55 bridges, 16 subways, 35 footbridges, 10 culverts, 58 retaining walls)</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Road gullies</td>
                  <td className="px-6 py-4 text-center font-medium">23,000+</td>
                </tr>
                <tr>
                  <td className="px-6 py-4">Street lighting columns</td>
                  <td className="px-6 py-4 text-center font-medium">19,500+ (LED)</td>
                </tr>
              </tbody>
            </table>
          </div>

          <QuoteCard
            quote="Bexley does not maintain any trunk roads or motorways. The major roads of the A2 and A20 fall under the responsibility of Transport for London (TfL), as part of their strategic road network."
            source="London Borough of Bexley Local Highways Maintenance Transparency Report 2025"
          />
        </div>
      </section>

      {/* Condition data */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What GREEN Condition Actually Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of condition data — Bexley maps a five-category assessment to the DfT&apos;s red/amber/green system
          </p>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
            <p className="text-amber-800 text-sm">
              <strong>Methodology caveat:</strong> Bexley has adopted newer condition assessment technology rather than
              traditional SCANNER surveys alone. Its five-category method maps to RAG as: Green = categories 1–2,
              Amber = categories 3–4, Red = category 5. The transparency report publishes full red/amber/green tables
              for A and B/C roads, but for U-roads publishes <strong>RED percentages only</strong> — not amber or
              green breakdowns for the 450.79km where most claims start.
            </p>
          </div>

          {/* A roads */}
          <div className="bg-white border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A roads (79.93km)</h3>
            <div className="bg-surface-50 rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">0.86%</td>
                    <td className="px-4 py-2 text-center">8.38%</td>
                    <td className="px-4 py-2 text-center">90.76%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">0.38%</td>
                    <td className="px-4 py-2 text-center">4.48%</td>
                    <td className="px-4 py-2 text-center">95.14%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">1.36%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">12.91%</td>
                    <td className="px-4 py-2 text-center font-bold">85.73%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              A-road RED/AMBER combined rose from 9.24% (2020) to 14.27% (2024) — roughly 11.4km of principal roads
              needing attention. Some A-road corridors are maintained by TfL, not the borough council.
            </p>
          </div>

          {/* B&C roads */}
          <div className="bg-white border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">B and C roads (39.90km)</h3>
            <div className="bg-surface-50 rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">0.00%</td>
                    <td className="px-4 py-2 text-center">10.03%</td>
                    <td className="px-4 py-2 text-center">89.97%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">0.86%</td>
                    <td className="px-4 py-2 text-center">6.09%</td>
                    <td className="px-4 py-2 text-center">93.05%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">3.38%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">16.37%</td>
                    <td className="px-4 py-2 text-center font-bold">80.25%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              B/C RED/AMBER combined rose from 10.03% to 19.75% — roughly 7.9km of key roads. RED condition on this
              small network climbed from zero in 2020 to 3.38% in 2024.
            </p>
          </div>

          {/* U roads */}
          <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 mb-8">
            <h3 className="font-semibold text-rose-900 mb-4">Unclassified roads (450.79km) — RED condition only published</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-rose-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-rose-100 border-b border-rose-200">
                    <th className="px-4 py-2 text-left font-semibold text-rose-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">2.31%</td>
                  </tr>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">3.72%</td>
                  </tr>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center text-amber-600">6.92%</td>
                  </tr>
                  <tr className="bg-rose-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">12.10%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-rose-800 font-medium">
              U-road RED condition more than quintupled from 2.31% to 12.10% — roughly 54.5km of residential network
              the council defines as poor. The report attributes increased 2023 and 2024 levels partly to defects from
              December 2022 severe weather that &ldquo;slowly deteriorated with weather and use.&rdquo;
            </p>
          </div>

          <QuoteCard
            quote="It is neither practical nor affordable to keep all roads and footways in a perfectly smooth, defect-free condition at all times. Instead, we apply recognised threshold measurements and risk-based assessments to decide which defects need repairs."
            source="London Borough of Bexley Local Highways Maintenance Transparency Report 2025"
          />
        </div>
      </section>

      {/* Spending */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold mb-4">
            Following the Money
          </h2>
          <p className="text-brand-200 mb-8 max-w-2xl">
            GREEN spend — but preventative share is falling back towards reactive maintenance
          </p>

          <div className="bg-brand-900/50 rounded-2xl border border-brand-800 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <caption className="sr-only">Bexley highway maintenance spending 2020-2026</caption>
              <thead>
                <tr className="bg-brand-800 border-b border-brand-700">
                  <th className="px-4 py-3 text-left font-semibold text-brand-300">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">DfT capital (£000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Capital spend (£000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Revenue spend (£000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Preventative</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Reactive</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-800">
                <tr className="bg-amber-900/30">
                  <td className="px-4 py-3 font-medium">2025/26 (proj.)</td>
                  <td className="px-4 py-3 text-center">895</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-400">4,510</td>
                  <td className="px-4 py-3 text-center">1,936</td>
                  <td className="px-4 py-3 text-center text-amber-400">61%</td>
                  <td className="px-4 py-3 text-center text-amber-400">39%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2024/25</td>
                  <td className="px-4 py-3 text-center">275</td>
                  <td className="px-4 py-3 text-center">3,281</td>
                  <td className="px-4 py-3 text-center">1,380</td>
                  <td className="px-4 py-3 text-center">66%</td>
                  <td className="px-4 py-3 text-center">34%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2023/24</td>
                  <td className="px-4 py-3 text-center">275</td>
                  <td className="px-4 py-3 text-center">4,218</td>
                  <td className="px-4 py-3 text-center">1,411</td>
                  <td className="px-4 py-3 text-center">71%</td>
                  <td className="px-4 py-3 text-center">29%</td>
                </tr>
                <tr className="bg-emerald-900/30">
                  <td className="px-4 py-3">2022/23</td>
                  <td className="px-4 py-3 text-center">—</td>
                  <td className="px-4 py-3 text-center">2,871</td>
                  <td className="px-4 py-3 text-center">1,119</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-400">72%</td>
                  <td className="px-4 py-3 text-center">28%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2021/22</td>
                  <td className="px-4 py-3 text-center">—</td>
                  <td className="px-4 py-3 text-center">2,440</td>
                  <td className="px-4 py-3 text-center">1,882</td>
                  <td className="px-4 py-3 text-center">56%</td>
                  <td className="px-4 py-3 text-center">44%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2020/21</td>
                  <td className="px-4 py-3 text-center">—</td>
                  <td className="px-4 py-3 text-center">1,507</td>
                  <td className="px-4 py-3 text-center">2,827</td>
                  <td className="px-4 py-3 text-center">35%</td>
                  <td className="px-4 py-3 text-center">65%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-emerald-500/20 rounded-xl p-6 border border-emerald-500/30">
              <h3 className="font-semibold text-white mb-3">Why spend is GREEN</h3>
              <p className="text-brand-200 text-sm">
                Projected 2025/26 capital spend of £4.51m is more than five times the £895,000 DfT allocation.
                Total highways spend (capital plus revenue) is projected at £6.446m. The council supplements government
                funding substantially from its own budgets.
              </p>
            </div>
            <div className="bg-amber-500/20 rounded-xl p-6 border border-amber-500/30">
              <h3 className="font-semibold text-white mb-3">Why claims still happen</h3>
              <p className="text-brand-200 text-sm">
                Preventative share peaked at 72% in 2022/23 but is projected to fall to 61% in 2025/26 — with reactive
                maintenance rising to 39%, the highest reactive proportion since 2021/22. Spend volume does not prove
                every defect was caught within inspection intervals.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Rising Pothole Patch Counts
          </h2>
          <p className="text-surface-600 mb-8">
            Estimated potholes filled from reactive maintenance — the council records total defects treated, then
            estimates pothole counts from standard working practices
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <caption className="sr-only">Bexley estimated potholes filled 2020-2025</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Area treated (m²)</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Change vs 2020/21</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">1,450</td>
                  <td className="px-6 py-4 text-center">1,414</td>
                  <td className="px-6 py-4 text-center text-surface-500">Baseline</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">1,480</td>
                  <td className="px-6 py-4 text-center">1,786</td>
                  <td className="px-6 py-4 text-center text-amber-600">+26.3%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">2,638</td>
                  <td className="px-6 py-4 text-center">2,032</td>
                  <td className="px-6 py-4 text-center text-amber-600">+43.7%</td>
                </tr>
                <tr className="bg-rose-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center">3,047</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">2,595</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">+83.5%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">2,353</td>
                  <td className="px-6 py-4 text-center font-bold">2,590</td>
                  <td className="px-6 py-4 text-center text-amber-600">+83.2%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <QuoteCard
              quote="The table shows that the number of potholes filled has steadily increased from 2021 to 2022 to 2023 to 2024 from 1,414 to 2,595; an increase of 83%. The figure for 2024 to 2025 is very similar to last year, showing that this high level of repair work is being maintained."
              source="London Borough of Bexley Local Highways Maintenance Transparency Report 2025"
            />
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Over seven patches a day</h3>
              <p className="text-surface-600 text-sm">
                2,590 pothole repairs in 2024/25 works out to roughly 7.1 patches per day across 570.62km. The council
                expects around 1,600 urgent (P1) and 400 non-urgent (P2) pothole repairs in 2025/26 — defects forming
                faster than preventative resurfacing prevents them on a network where U-road RED condition hit 12.10%.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Inspections and Section 58 */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Inspections, Surveys and Section 58
          </h2>
          <p className="text-surface-600 mb-8">
            How Bexley says it knows the condition of its network — and where gaps appear
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Survey and inspection frequency</h3>
              <ul className="text-surface-600 text-sm space-y-2">
                <li>• <strong>All roads:</strong> manually inspected at least twice a year</li>
                <li>• <strong>High pedestrian areas:</strong> town centres and shopping areas inspected more frequently</li>
                <li>• <strong>Camera surveys:</strong> each road undergoes vehicle-based camera inspection at least once a year</li>
                <li>• <strong>Condition assessment:</strong> five-category method mapped to DfT red/amber/green</li>
                <li>• <strong>FixMyStreet:</strong> around 16,000 resident reports handled each year</li>
              </ul>
            </div>
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Concrete slab roads</h3>
              <p className="text-surface-600 text-sm mb-3">
                Many Bexley residential roads use concrete slabs with a thin 15–25mm asphalt overlay. Reflective cracking
                at expansion joints may fall below the council&apos;s intervention thresholds — the report states visible
                cracks &ldquo;do not indicate structural failure&rdquo; and patches revealing concrete beneath may not
                require immediate repair.
              </p>
              <p className="text-surface-600 text-sm">
                If your pothole exceeded intervention thresholds or caused vehicle damage, threshold policy alone does
                not defeat a claim supported by photos, prior reports and repair records.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8 mb-8">
            <h3 className="font-display text-xl font-bold mb-4">Section 41 vs Section 58</h3>
            <p className="text-brand-200 mb-4">
              Under <strong className="text-white">Section 41 of the Highways Act 1980</strong>, Bexley must maintain
              public highways. To defend a claim under <strong className="text-white">Section 58</strong>, it must show a
              reasonable system for inspecting and repairing the specific defect — not just publish GREEN DfT scorecards.
            </p>
            <ul className="text-brand-300 space-y-2 text-sm">
              <li>• Was your road inspected within the twice-yearly or camera survey cycle?</li>
              <li>• Did the defect meet intervention criteria during routine inspections?</li>
              <li>• Were there prior FixMyStreet or council reports giving actual notice?</li>
              <li>• Does photographic evidence show defect age beyond the inspection interval?</li>
            </ul>
          </div>

          <QuoteCard
            quote="The increased levels for 2023 and 2024 is attributed in part to the legacy impacts of the severe weather of December 2022, where small defects formed and have slowly deteriorated with weather and use."
            source="London Borough of Bexley Local Highways Maintenance Transparency Report 2025"
          />
        </div>
      </section>

      {/* 2025/26 plans */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Planned Work 2025/26
          </h2>
          <p className="text-surface-600 mb-8">
            What Bexley says it will deliver this financial year — against identified backlog
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-emerald-600">8.6km</div>
              <div className="text-sm text-emerald-800 font-medium mt-1">Carriageway resurfacing</div>
              <div className="text-xs text-emerald-700 mt-1">Plus 1km footway if all schemes complete</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">40–50</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Large patching schemes</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-amber-600">~2,000</div>
              <div className="text-sm text-amber-800 font-medium mt-1">Expected pothole repairs</div>
              <div className="text-xs text-amber-700 mt-1">~1,600 P1 urgent + ~400 P2</div>
            </div>
            <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-rose-600">73.8km</div>
              <div className="text-sm text-rose-800 font-medium mt-1">Roads in RED/AMBER condition</div>
              <div className="text-xs text-rose-700 mt-1">Identified at last survey</div>
            </div>
          </div>

          <QuoteCard
            quote="Therefore, it is not uncommon for Bexley to postpone resurfacing works to allow utility companies to carry out their planned works, so that these essential services for residents and businesses such as water, gas, electricity, and telecommunications are maintained."
            source="London Borough of Bexley Local Highways Maintenance Transparency Report 2025"
          />

          <div className="mt-6 bg-surface-50 rounded-xl p-6 border border-surface-200">
            <h3 className="font-semibold text-brand-900 mb-3">Coverage maths</h3>
            <p className="text-surface-600 text-sm">
              Bexley identifies roughly 73.8km of roads in RED or AMBER condition (54.5km U-road RED, 11.4km A-road
              RED/AMBER, 7.9km B/C RED/AMBER). Planned carriageway resurfacing covers 8.6km — about 11.7% of that
              backlog in a single year. At that rate, clearing identified poor-condition network would take around 8.6
              years, assuming no new deterioration — which pothole data and rising U-road RED percentages suggest is
              unlikely.
            </p>
          </div>
        </div>
      </section>

      {/* Council admissions */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What Bexley Acknowledges
          </h2>
          <p className="text-surface-600 mb-8">
            Verbatim admissions from the 2025 transparency report
          </p>

          <div className="space-y-6">
            <div>
              <h3 className="font-semibold text-brand-900 mb-3">On December 2022 weather legacy</h3>
              <QuoteCard
                quote="The increased levels for 2023 and 2024 is attributed in part to the legacy impacts of the severe weather of December 2022, where small defects formed and have slowly deteriorated with weather and use."
                source="London Borough of Bexley Local Highways Maintenance Transparency Report 2025"
              />
            </div>

            <div>
              <h3 className="font-semibold text-brand-900 mb-3">On pandemic disruption to prevention</h3>
              <QuoteCard
                quote="2020 to 2021 was the start of the Covid-19 pandemic lockdown restrictions, which resulted in reduced expenditure in planned resurfacing works, with a consequent increase in revenue expenditure to deal with road defects when necessary to maintain a safe road network."
                source="London Borough of Bexley Local Highways Maintenance Transparency Report 2025"
              />
            </div>

            <div>
              <h3 className="font-semibold text-brand-900 mb-3">On utility delays to resurfacing</h3>
              <QuoteCard
                quote="Therefore, it is not uncommon for Bexley to postpone resurfacing works to allow utility companies to carry out their planned works, so that these essential services for residents and businesses such as water, gas, electricity, and telecommunications are maintained and that it minimises the times that our new road surface is dug up soon after it is installed."
                source="London Borough of Bexley Local Highways Maintenance Transparency Report 2025"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Balanced claims section */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a GREEN-Rated Borough
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Bexley invests heavily and scores GREEN on condition and spend — here is how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-emerald-50 rounded-2xl p-6 border border-emerald-200">
              <h3 className="font-semibold text-emerald-900 mb-4">What works in the council&apos;s favour</h3>
              <ul className="text-emerald-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN condition and spend DfT scorecards
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Capital spend projected at ~5× DfT allocation (£4.51m vs £895k)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Twice-yearly foot inspections plus annual camera surveys on all roads
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> FixMyStreet handling ~16,000 reports a year
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Two full-time repair gangs plus out-of-hours emergency response
                </li>
              </ul>
              <p className="text-emerald-700 text-sm mt-4 italic">
                Expect a well-prepared Section 58 defence. Generic &ldquo;council neglect&rdquo; arguments will not land.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What works in yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER overall and best-practice scorecards from DfT
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> U-road RED condition up 424% — 54.5km needing maintenance at last survey
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 2,590 pothole fills in 2024/25 — up 83% since 2020/21
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Preventative share falling from 72% peak to 61% projected
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council admits December 2022 defects &ldquo;slowly deteriorated&rdquo; into potholes
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Resurfacing postponed for utility works — 8.6km planned against 73.8km backlog
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The winning strategy here is specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a borough with GREEN condition and spend scorecards, your claim lives or dies on the{' '}
              <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior FixMyStreet or council reports — proof of actual notice beyond network surveys</li>
              <li>• Photos showing defect size, depth and age (weathered edges, previous patching)</li>
              <li>• Road class — on a U-road, rising RED condition (12.10%) and incomplete published RAG data</li>
              <li>• Whether the defect exceeded concrete-slab intervention thresholds or met pothole repair criteria</li>
            </ul>
            <p className="text-white font-medium">
              Fixtyer builds exactly this case: prior-report search, photo assessment, and citations from Bexley&apos;s
              own transparency data where it helps you — without pretending the council is failing overall.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-12 bg-surface-100 border-y border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-3">
            Report a Pothole to London Borough of Bexley
          </h2>
          <p className="text-surface-600 mb-6">
            Reporting a defect creates a record the council had notice. Do this before claiming — and tell us when you
            reported it so we can reference it in your pack.
          </p>
          <a
            href="https://fix.bexley.gov.uk/report/new"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-6 py-3 bg-brand-600 text-white font-semibold rounded-xl hover:bg-brand-700 transition-colors"
          >
            Report a road defect — fix.bexley.gov.uk
            <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Bexley?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A green-rated borough demands a precise claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No U-road deterioration argument</li>
                <li>• No prior FixMyStreet search</li>
                <li>• No concrete-slab threshold challenge</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional claim pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ U-road RED condition rise documented</li>
                <li>✅ 2,590 pothole fills in 2024/25 cited</li>
                <li>✅ Preventative decline and backlog maths</li>
                <li>✅ Prior FixMyStreet reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Bexley</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=bexley"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Bexley Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View all council ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently asked questions
          </h2>

          <div className="space-y-6">
            {faqs.map((faq) => (
              <div key={faq.question} className="bg-white rounded-xl p-6 border border-surface-200">
                <h3 className="font-semibold text-brand-900 mb-2">{faq.question}</h3>
                <p className="text-surface-600 text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.bexley.gov.uk/services/parking-transport-and-streets/potholes-or-street-defects/reporting-pothole-and-road-defect-issues/highways-maintenance-report"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              London Borough of Bexley Local Highways Maintenance Transparency Report (June 2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
